<!-- Panel contents for download data options. Parent owns container element and styling of the container. -->
<template>
  <h4 class="subtitle mb-3 is-6 is-small-mobile">Download</h4>

  <div class="field has-text-left">
    <div class="control">
      <div class="select is-fullwidth">
        <select id="bulk-data-type" v-model="store.selectedDataType">
          <option
            v-for="dt in DATA_TYPES"
            :key="dt.value"
            :value="dt.value"
            :class="{
              'hide-personal': ['labels', 'ingredient_lists', 'allergens', 'costs'].includes(
                dt.value
              ),
            }"
          >
            {{ dt.label }}
          </option>
        </select>
      </div>
    </div>
    <div v-if="store.selectedDataType === LABELS_DATA && !isOnBatchDownloadPage" class="mt-2">
      You can render label images in bulk <a :href="batchDownloadPath(userId)">here</a>.
    </div>
  </div>

  <div v-if="store.selectedDataType === NUTRITION_BREAKDOWN" class="field has-text-left mb-4">
    <label class="label is-small-mobile" for="daily-values-format">Daily Values</label>
    <div class="control">
      <div class="select is-fullwidth">
        <select id="daily-values-format" v-model="store.selectedDailyValuesFormat">
          <option v-for="item in dailyValuesOptions" :key="item" :value="item">
            {{ item }}
          </option>
        </select>
      </div>
    </div>

    <div class="field mt-3">
      <label class="checkbox label-color is-small-mobile">
        <input v-model="store.expandSubrecipes" type="checkbox" class="mr-05" />
        Expand subrecipes
      </label>
    </div>
  </div>

  <div v-if="store.selectedDataType === COSTS" class="field has-text-left mb-4">
    <label class="label is-hidden">Costs Mode:</label>
    <div class="control">
      <label v-for="mode in COST_MODES" :key="mode.value" class="radio label-color">
        <input v-model="store.costsMode" type="radio" :value="mode.value" class="mr-05" />
        {{ mode.label }}
      </label>
    </div>
  </div>

  <template v-if="store.selectedDataType === MENU">
    <div class="field has-text-left mb-4">
      <label class="label is-small-mobile" for="menu-format">Format</label>
      <div class="control">
        <div class="select is-fullwidth">
          <select id="menu-format" v-model="store.selectedMenuFormat">
            <option value="csv">CSV</option>
            <option value="pdf">PDF</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="store.selectedMenuFormat === PDF" class="field has-text-left mb-4">
      <label class="label is-small-mobile" for="menu-title">Custom Title</label>
      <div class="control">
        <input
          id="menu-title"
          v-model="store.pdfTitle"
          type="text"
          class="input is-fullwidth"
          placeholder="My awesome menu"
        />
      </div>
    </div>
  </template>

  <WrapWithHint
    :class="buttonContainerClass"
    :content="!selectedIds.length ? 'Select recipes first' : null"
  >
    <div
      :style="{ cursor: selectedIds.length === 0 ? 'not-allowed' : 'auto' }"
      @click="showBulkSelectionHint()"
    >
      <button
        id="download-data-button"
        class="button is-small-mobile"
        :class="{
          'is-loading': isBusy,
          'is-primary': downloadButtonColorIsPrimary && selectedIds.length > 0,
          'is-info': !downloadButtonColorIsPrimary && selectedIds.length > 0,
          'is-outlined': selectedIds.length === 0,
          'click-disabled': selectedIds.length === 0,
          'half-opacity': selectedIds.length === 0,
        }"
        @click="handleDownload"
      >
        Download <template v-if="store.selectedDataType !== MENU">spreadsheet</template>
      </button>
    </div>
  </WrapWithHint>
</template>

<script setup>import { ref as _ref } from 'vue';

  import WrapWithHint from "../wrap-with-hint.vue";

  import { Flash } from "~/jose_modern/flash";
  import { makeStashedFileRequest } from "~/jose_modern/stashed_file";
  import { recipeDataBatchDownloadPath, batchDownloadPath } from "~/routes";
  import { errorHelpTip } from "../lib/constants";

  import { useBulkDataDownloadStore } from "../stores/bulk-data-download-store";
  import {
    NUTRITION_BREAKDOWN,
    COSTS,
    DATA_TYPES,
    COST_MODES,
    LABELS_DATA,
    MENU,
    PDF,
  } from "../stores/constants";

  import { onMounted } from "vue";

  const flashError = (msg) => Flash.error(msg);

  const props = defineProps({
    selectedIds: { type: Array, required: true },
    userId: { type: String, required: true },
    dailyValuesOptions: { type: Array, required: true },
    downloadButtonColorIsPrimary: { type: Boolean, default: true },
    buttonContainerClass: { type: String, default: "" },
  });

  const emit = defineEmits(["done"]);

  let isBusy = _ref(false);
  let isOnBatchDownloadPage = _ref(false);

  const store = useBulkDataDownloadStore();

  function showBulkSelectionHint() {
    if (props.selectedIds.length === 0) {
      document.dispatchEvent(new CustomEvent("recipe-bulk-selection:hint"));
    }
  }
  onMounted(() => {
    store.initialize(props.dailyValuesOptions);
    isOnBatchDownloadPage.value = window.location.pathname.startsWith(batchDownloadPath(props.userId));
  });

  async function handleDownload() {
    isBusy.value = true;
    try {
      const requestData = {
        recipe_ids: props.selectedIds,
        data_type: store.selectedDataType,
      };

      switch (store.selectedDataType) {
        case COSTS:
          requestData.mode = store.costsMode;
          break;
        case NUTRITION_BREAKDOWN:
          requestData.daily_values = store.selectedDailyValuesFormat;
          requestData.expand_subrecipes = store.expandSubrecipes;
          break;
        case MENU:
          requestData.format = store.selectedMenuFormat;
          requestData.pdf_title = store.pdfTitle;
          break;
      }

      const { ok, error, handled } = await makeStashedFileRequest(
        recipeDataBatchDownloadPath(props.userId),
        {
          data: { recipe_data: requestData },
          autoDownload: true,
          method: "POST",
        }
      );

      if (ok) {
        // `stashed_file_ui.js` handles flashSuccess("File generated")
        console.debug("Starting data download.");
      } else if (!handled) {
        // `handled` means stashed_file_ui.js already surfaced the file's error message
        flashError(error || `Failed to download data. ${errorHelpTip}`);
      }
    } catch (err) {
      console.error(err);
      if (typeof err === "string") {
        flashError(err);
      } else {
        flashError(`Failed to download. ${errorHelpTip}`);
      }
    }
    isBusy.value = false;
    emit("done");
  }
</script>

<style scoped lang="scss">
  .label-color {
    color: #3f4a56; // same color as .label
  }

  .radio {
    user-select: none;
  }

  .radio:first-child,
  .radio + .radio {
    margin-right: 0.5rem;
    margin-left: 0;
  }

  .mr-05 {
    margin-right: 0.125rem;
  }
</style>
